import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import axios from "axios";
import { Button, Divider } from "@mui/material";
import { Print } from "@mui/icons-material";
import LoadingPanel from "../../components/common/LoadingPanel";
import ErrorMessage from "../../components/common/ErrorMessage";
import localize from "../../common/localize";
import { decryptForUrl } from "../../common/utils";
import useGlobalContext from "../../hooks/useGlobalContext";
import SettlementReportBody from "./components/SettlementReportBody";
import SettlementReportSummary from "./components/SettlementReportSummary";

const normalize = (body) =>
  Array.isArray(body) ? body : (body?.Data ?? body?.responseData ?? []);

const parseFilterParam = (searchParams) => {
  const raw = searchParams?.get("filter");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decryptForUrl(raw));
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch (e) {
    return null;
  }
};

// Renders every settlement's report stacked in a single tab, each below the
// previous one, with a print button that prints them all together.
export default function SettlementReportAll() {
  const { config, SETTLEMENT_ENDPOINT } = useGlobalContext();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  const componentRef = useRef();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const filter = parseFilterParam(searchParams);
        const buildConfig = (page) => ({
          ...config,
          headers: {
            ...config?.headers,
            pagination: page,
            Filter: filter ? JSON.stringify(filter) : null,
          },
        });
        // The settlement list is paginated by page index; walk every page.
        const firstResp = await axios.get(
          `${SETTLEMENT_ENDPOINT}`,
          buildConfig(0),
        );
        const firstBody = firstResp?.data;
        let settlements = normalize(firstBody);
        const count = firstBody?.Count ?? settlements.length;
        const pageSize = settlements.length;
        if (pageSize > 0 && count > pageSize) {
          const totalPages = Math.ceil(count / pageSize);
          const restResps = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              axios.get(`${SETTLEMENT_ENDPOINT}`, buildConfig(i + 1)),
            ),
          );
          restResps.forEach((r) => {
            settlements = settlements.concat(normalize(r?.data));
          });
        }
        const withDetails = await Promise.all(
          settlements.map(async (settlement) => {
            try {
              const detResp = await axios.get(
                `${SETTLEMENT_ENDPOINT}/${settlement.Uuid}/detail`,
                config,
              );
              return { settlement, details: normalize(detResp?.data) };
            } catch {
              return { settlement, details: [] };
            }
          }),
        );
        if (!mountedRef.current) return;
        setItems(withDetails);
      } catch (error) {
        if (!mountedRef.current) return;
        setErrorMessage(error?.message ?? localize.error_unexpected);
        setShowErrorModal(true);
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    };
    load();
  }, []);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@media print {
      @page {
        size: 600mm 500mm;
        margin: 0;
      }
      a[href]:after {
        content: none !important;
      }
      .print-row {
        break-inside: avoid;
      }
      .settlement-report-block {
        break-after: page;
      }
    }`,
  });

  if (isLoading) {
    return <LoadingPanel text={localize.loading} />;
  }

  return (
    <div className="w-full flex items-center justify-center align-center">
      <div className="w-full flex-col" style={{ width: "80%" }}>
        <div className="w-full flex-col" ref={componentRef}>
          {showErrorModal && (
            <ErrorMessage
              message={errorMessage}
              title={localize.error_unexpected}
              showModal={showErrorModal}
              setShowModal={setShowErrorModal}
            />
          )}

          {items.length > 0 ? (
            <>
              <div className="settlement-report-block">
                <SettlementReportSummary
                  details={items.flatMap((it) => it.details)}
                />
              </div>
              {items.map((item, index) => (
                <div
                  key={item.settlement?.Uuid ?? index}
                  className="settlement-report-block"
                  style={{ marginBottom: "24px" }}
                >
                  <SettlementReportBody
                    settlement={item.settlement}
                    details={item.details}
                  />
                  {index < items.length - 1 && (
                    <Divider sx={{ my: 3, borderBottomWidth: 2 }} />
                  )}
                </div>
              ))}
            </>
          ) : (
            <label className="report_little_text">{localize.no_data}</label>
          )}
        </div>

        <div className="flex w-full justify-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handlePrint}
            sx={{
              backgroundColor: "var(--app-bg, #10366e)",
              color: "var(--app-fg, #ffffff)",
              "&:hover": { backgroundColor: "var(--app-bg, #10366e)" },
            }}
            style={{ marginTop: "20px", marginBottom: "30px" }}
          >
            <span className="mr-2">{localize.print}</span>
            <Print />
          </Button>
        </div>
      </div>
    </div>
  );
}
